/**
 * Onglet Documents - Profil Acheteur
 *
 * Structure visuelle identique au prestataire (TabDocuments) :
 * - Chapitres avec DocumentsList pour les docs existants
 * - Même pattern boutons Ajouter / Actualiser
 * - Logo identique
 */
import React, { useState } from 'react';
import Button from '../../../composants/Button/Button';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import UploadLogoModal from '../../../composants/UploadLogoModal/UploadLogoModal';
import UploadDocumentModal from '../../../composants/UploadDocumentModal/UploadDocumentModal';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/export.svg';
import { useAcheteurProfile } from '../../../contexts/AcheteurProfileContext';
import type { AcheteurDocument } from '../../../contexts/AcheteurProfileContext';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import '../../../composants/MesDocuments/MesDocuments.css';
import './TabDocuments.css';

interface DocumentSlot {
  type: string;
  label: string;
  required?: boolean;
}

const DOCUMENT_SLOTS: DocumentSlot[] = [
  { type: 'habilitation_admin', label: "Habilitation de l'administrateur du compte", required: true },
  { type: 'convention_deliberation', label: 'Délibération (facultatif)', required: false },
];

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2 Mo

const TabDocumentsAcheteur: React.FC = () => {
  const { logoUrl, documents, bibliographie, setLogoUrl, setDocuments } = useAcheteurProfile();

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  const [isLogoDragOver, setIsLogoDragOver] = useState(false);

  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [docErrors, setDocErrors] = useState<Record<string, string | null>>({});
  const [docModalOpenType, setDocModalOpenType] = useState<string | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Accept': 'application/json',
    };
  };

  // La pastille sidebar et la barre de la page lisent toutes deux le calcul local de Profil.tsx
  // (pousse dans AcheteurProfileContext). Mettre a jour logoUrl / documents via le contexte suffit
  // a rafraichir les deux ; pas de refetch backend ici (il pourrait desynchroniser la pastille).
  const LOGO_ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  const handleLogoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsLogoDragOver(false);
    const file = Array.from(e.dataTransfer.files).find(f => LOGO_ACCEPTED_TYPES.includes(f.type));
    if (!file) { setLogoError('Format non accepté. Utilisez JPG, PNG, GIF ou WebP.'); return; }
    if (file.size > 2 * 1024 * 1024) { setLogoError('Le fichier est trop volumineux (max 2 Mo).'); return; }
    try {
      await handleLogoUploadFromModal(file);
    } catch { /* error already set in handleLogoUploadFromModal */ }
  };

  // Upload du logo (appelé par UploadLogoModal)
  const handleLogoUploadFromModal = async (file: File) => {
    setLogoError(null);
    setIsUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/acheteur-profile/logo`, {
        method: 'POST',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Erreur lors de l'upload du logo");
      }

      const data = await response.json();
      setLogoUrl(data.data?.logo_url || null);
    } catch (error: any) {
      setLogoError(error.message || "Erreur lors de l'upload du logo");
      throw error;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Suppression du logo
  const handleDeleteLogo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) return;

    setLogoError(null);
    setIsDeletingLogo(true);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/acheteur-profile/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression du logo');
      }

      setLogoUrl(null);
    } catch (error: any) {
      setLogoError(error.message || 'Erreur lors de la suppression du logo');
    } finally {
      setIsDeletingLogo(false);
    }
  };

  // Upload d'un document (logique partagée entre input et drag-and-drop)
  const uploadDocumentFile = async (file: File, type: string) => {
    setUploading(prev => ({ ...prev, [type]: true }));
    setDocErrors(prev => ({ ...prev, [type]: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiUrl}/acheteur-profile/documents`, {
        method: 'POST',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'upload');
      }

      const data = await response.json();
      const doc: AcheteurDocument = data.data?.document || data.document;
      setDocuments(prev => ({ ...prev, [type]: doc }));
    } catch (error: any) {
      setDocErrors(prev => ({ ...prev, [type]: error.message || 'Erreur lors de l\'upload' }));
      throw error;
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Téléchargement géré par DocumentsList via downloadUrlPrefix

  const isLogoLoading = isUploadingLogo || isDeletingLogo;

  // Rendu d'une section document
  const renderDocumentSection = (slot: DocumentSlot) => {
    const doc = documents[slot.type];
    const isUploading = uploading[slot.type] ?? false;
    const docError = docErrors[slot.type];

    const formattedDocs = doc
      ? [{
        id: doc.id,
        type: slot.label,
        updated_at: doc.updated_at,
        key: doc.key,
        category: 'entreprise' as const,
        filename: doc.filename || `${slot.type}.pdf`,
      }]
      : [];

    return (
      <section key={slot.type} className="document-section">

        <div className={`section-content${!doc ? ' section-content--empty' : ''}`} style={{ width: '100%', boxSizing: 'border-box' }}>
          <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
            {slot.label}
            {slot.required && <span className="required-asterisk">*</span>}
          </h2>
          <DocumentsList
            documents={formattedDocs}
            variant="inline"
            iconColor="green"
            hideLabel
            cardStyle="square"
            downloadUrlPrefix="acheteur-profile/documents"
            onAdd={() => setDocModalOpenType(slot.type)}
            onAddDisabled={isUploading}
            addCardError={!doc && !!slot.required}
            onDrop={async (file) => { await uploadDocumentFile(file, slot.type); }}
            onModify={() => setDocModalOpenType(slot.type)}
          />

          {docError && (
            <p className="logo-error-text" style={{ marginTop: '0.5rem' }}>{docError}</p>
          )}
        </div>

        <UploadDocumentModal
          isOpen={docModalOpenType === slot.type}
          onClose={() => setDocModalOpenType(null)}
          onUpload={async (file) => {
            await uploadDocumentFile(file, slot.type);
            setDocModalOpenType(null);
          }}
          preselectedType={slot.label}
        />
      </section>
    );
  };

  return (
    <div className="mes-documents">
      {/* Chapitre : Documents à fournir */}
      <SectionPliable title="Documents à fournir">
        <div className="chapter-content chapter-content--grid4">

          {/* Logo de l'organisme */}
          <section className="document-section">
            <div className="section-content">
              <h2 className="subsection-header">
                Logo de l'organisme
                <span className="required-asterisk">*</span>
              </h2>
              <div
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsLogoDragOver(true); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsLogoDragOver(false); }}
                onDrop={handleLogoDrop}
                style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}
              >
                {/* Carte logo existant */}
                {logoUrl && (
                  <div
                    className="document-card logo-card"
                    onClick={() => setLogoPreviewOpen(true)}
                    title="Prévisualiser"
                  >
                    <div className="logo-card-thumb">
                      <img
                        src={logoUrl}
                        alt="Logo organisme"
                        className="logo-card-img"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  </div>
                )}

                {/* Modale preview logo */}
                {logoPreviewOpen && logoUrl && (
                  <div className="modal-overlay" onClick={() => setLogoPreviewOpen(false)}>
                    <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
                      <h3>Logo de l'organisme</h3>
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                        <img
                          src={logoUrl}
                          alt="Logo organisme"
                          style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }}
                        />
                      </div>
                      <div className="modal-actions">
                        <Button
                          onClick={() => { setLogoPreviewOpen(false); setLogoModalOpen(true); }}
                          value="Modifier"
                          size="small"
                          variant="secondary"
                        />
                        <Button
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = logoUrl;
                            a.download = 'logo';
                            a.target = '_blank';
                            a.click();
                          }}
                          icon={<DownloadIcon className="dl-button-icon-white" />}
                          value="Télécharger"
                          size="small"
                        />
                        <Button
                          onClick={() => setLogoPreviewOpen(false)}
                          value="Fermer"
                          size="small"
                          variant="secondary"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Carte "+" → uniquement si pas encore de logo */}
                {!logoUrl && (
                  <button
                    type="button"
                    className={`document-add-card${isLogoDragOver ? ' document-add-card--drag-over' : ''}${!logoUrl ? ' document-add-card--error' : ''}`}
                    onClick={() => setLogoModalOpen(true)}
                    disabled={isLogoLoading}
                    title="Ajouter un logo"
                  >
                    {isLogoLoading ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <span className="document-add-card-plus">+</span>
                        <span className="document-add-card-label">Ajouter</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {logoError && (
                <p className="logo-error-text" style={{ marginTop: '0.5rem' }}>{logoError}</p>
              )}
            </div>

            <UploadLogoModal
              isOpen={logoModalOpen}
              onClose={() => setLogoModalOpen(false)}
              onUpload={handleLogoUploadFromModal}
            />
          </section>

          {/* Documents PDF */}
          {DOCUMENT_SLOTS.map(slot => renderDocumentSection(slot))}

        </div>
      </SectionPliable>

      {/* Chapitre : Bibliographie */}
      <SectionPliable title="Bibliographie">
        <div className="chapter-content chapter-content--grid4">
          {bibliographie.length === 0 ? (
            <p className="info-text" style={{ fontStyle: 'italic', gridColumn: '1 / -1' }}>
              Aucun document bibliographique pour le moment.
            </p>
          ) : (
            bibliographie.map(doc => (
              <section key={doc.id} className="document-section">
                <div className="section-content">
                  <h2 className="subsection-header" style={{ textAlign: 'center', width: '100%' }}>
                    {doc.nom}
                  </h2>
                  <DocumentsList
                    documents={[{
                      id: doc.id,
                      type: doc.nom,
                      updated_at: doc.updated_at,
                      category: 'commun' as const,
                      filename: doc.nom + '.pdf',
                    }]}
                    variant="inline"
                    iconColor="grey"
                    hideLabel
                    icon={
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                      </svg>
                    }
                  />
                </div>
              </section>
            ))
          )}
        </div>
      </SectionPliable>
    </div>
  );
};

export default TabDocumentsAcheteur;

